import { type FormEvent, useState } from 'react'
import { Loader2, Save, Wand2 } from 'lucide-react'
import type { SunoPromptGroup } from '../../../api/suno'
import { Button, IconButton, Modal, ModalActions, MultiSelect, type MultiSelectOption } from '../../../components/ui'
import styles from './PromptEditorDialog.module.css'

const cssClass = (...classNames: Array<string | undefined>): string =>
  classNames.filter(Boolean).join(' ')

export type PromptEditorState = {
  description: string
  excludePrompt: string
  groupIds: string[]
  id: string | null
  stylePrompt: string
  title: string
}

export const emptyPromptEditor = (): PromptEditorState => ({
  description: '',
  excludePrompt: '',
  groupIds: [],
  id: null,
  stylePrompt: '',
  title: ''
})

type PromptEditorDialogProps = {
  groups: SunoPromptGroup[]
  isSaving: boolean
  onCancel: () => void
  onChange: (value: PromptEditorState) => void
  onGenerateMetadata?: ((input: {
    excludePrompt: string
    stylePrompt: string
  }) => Promise<{ title: string; description: string }>) | undefined
  onSave: (value: PromptEditorState) => void
  saveError?: string | null | undefined
  value: PromptEditorState
}

export const PromptEditorDialog = ({
  groups,
  isSaving,
  onCancel,
  onChange,
  onGenerateMetadata,
  onSave,
  saveError,
  value
}: PromptEditorDialogProps) => {
  const titleValid = value.title.trim().length > 0
  const styleValid = value.stylePrompt.trim().length > 0
  const [metadataConfirmOpen, setMetadataConfirmOpen] = useState(false)
  const [metadataError, setMetadataError] = useState<string | null>(null)
  const [metadataLoading, setMetadataLoading] = useState(false)
  const groupOptions: Array<MultiSelectOption & { description: string | null }> = groups.map((group) => ({
    count: group.prompt_count,
    description: group.description,
    id: group.id,
    label: group.name
  }))

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    onSave(value)
  }

  const generateMetadata = async (): Promise<void> => {
    if (!onGenerateMetadata || !styleValid || metadataLoading) {
      return
    }

    setMetadataConfirmOpen(false)
    setMetadataLoading(true)
    setMetadataError(null)

    try {
      const result = await onGenerateMetadata({
        stylePrompt: value.stylePrompt.trim(),
        excludePrompt: value.excludePrompt.trim()
      })

      onChange({
        ...value,
        title: result.title,
        description: result.description
      })
    } catch (error) {
      setMetadataError(error instanceof Error ? error.message : 'Unable to generate title and description.')
    } finally {
      setMetadataLoading(false)
    }
  }

  const requestMetadataGeneration = (): void => {
    if (!onGenerateMetadata || !styleValid || metadataLoading) {
      return
    }

    if (value.title.trim() || value.description.trim()) {
      setMetadataConfirmOpen(true)
      return
    }

    void generateMetadata()
  }

  return (
    <>
      <Modal
        ariaLabelledBy="prompt-editor-title"
        bodyClassName={styles.body ?? ''}
        isDismissDisabled={isSaving || metadataConfirmOpen}
        mobilePresentation="sheet"
        onClose={onCancel}
        onSubmit={submit}
        panelClassName={cssClass(styles.panel)}
        title={(
          <div className={styles.headingCopy}>
            <h2 id="prompt-editor-title">{value.id ? 'Edit prompt' : 'Save prompt'}</h2>
            <p>{value.id ? 'Update the saved Suno prompt.' : 'Review the prompt before adding it to the library.'}</p>
          </div>
        )}
      >
        <div className={styles.fieldHeader}>
          <label className="form-label" htmlFor="suno-prompt-title">
            Title
          </label>
          {onGenerateMetadata && (
            <IconButton
              disabled={!styleValid || isSaving || metadataLoading}
              icon={metadataLoading ? <Loader2 className="tools-spin" size={17} /> : <Wand2 size={17} />}
              label="Generate title and description"
              onClick={requestMetadataGeneration}
              title="Generate title and description"
            />
          )}
        </div>
        <input
          className="form-input"
          id="suno-prompt-title"
          maxLength={160}
          onChange={(event) => onChange({ ...value, title: event.target.value })}
          placeholder="Night Drive Variant"
          value={value.title}
        />

        <label className="form-label" htmlFor="suno-prompt-description">
          Description (optional)
        </label>
        <textarea
          className={['form-input', styles.textarea].join(' ')}
          id="suno-prompt-description"
          maxLength={3000}
          onChange={(event) => onChange({ ...value, description: event.target.value })}
          rows={4}
          value={value.description}
        />
        {metadataError && <span className="form-error">{metadataError}</span>}

        <label className="form-label" htmlFor="suno-prompt-style">
          Style prompt
        </label>
        <textarea
          className={['form-input', styles.textarea].join(' ')}
          id="suno-prompt-style"
          maxLength={3000}
          onChange={(event) => onChange({ ...value, stylePrompt: event.target.value })}
          rows={5}
          value={value.stylePrompt}
        />

        <label className="form-label" htmlFor="suno-prompt-exclude">
          Exclude prompt (optional)
        </label>
        <textarea
          className={['form-input', styles.textarea].join(' ')}
          id="suno-prompt-exclude"
          maxLength={1000}
          onChange={(event) => onChange({ ...value, excludePrompt: event.target.value })}
          rows={3}
          value={value.excludePrompt}
        />

        <label className="form-label" htmlFor="suno-prompt-group-search">
          Groups
        </label>
        <MultiSelect
          className={styles.groupSelect}
          emptyText="No prompt groups found."
          filterOption={(group: MultiSelectOption & { description: string | null }, query: string) =>
            !query
            || group.label.toLowerCase().includes(query)
            || (group.description ?? '').toLowerCase().includes(query)}
          onChange={(groupIds: string[]) => onChange({ ...value, groupIds })}
          options={groupOptions}
          placeholder="Search prompt groups..."
          searchInputId="suno-prompt-group-search"
          selectedIds={value.groupIds}
        />

        {saveError && <span className="form-error">{saveError}</span>}
        <ModalActions>
          <Button disabled={isSaving} onClick={onCancel} variant="secondary">
            Cancel
          </Button>
          <Button disabled={!titleValid || !styleValid || isSaving} icon={isSaving ? <Loader2 className="tools-spin" size={16} /> : <Save size={16} />} type="submit" variant="primary">
            {isSaving ? 'Saving...' : 'Save prompt'}
          </Button>
        </ModalActions>
      </Modal>

      {metadataConfirmOpen && (
        <Modal
          ariaLabelledBy="prompt-overwrite-confirm-title"
          backdropClassName="prompt-overwrite-confirm"
          isDismissDisabled={metadataLoading}
          onClose={() => setMetadataConfirmOpen(false)}
          panelClassName={cssClass(styles.confirmPanel)}
          title={(
            <div className={styles.headingCopy}>
              <h2 id="prompt-overwrite-confirm-title">Overwrite title and description?</h2>
              <p>Generated values will replace the current title or description.</p>
            </div>
          )}
        >
          <ModalActions>
            <Button
              disabled={metadataLoading}
              onClick={() => setMetadataConfirmOpen(false)}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              disabled={metadataLoading}
              icon={metadataLoading ? <Loader2 className="tools-spin" size={16} /> : <Wand2 size={16} />}
              onClick={() => void generateMetadata()}
              variant="primary"
            >
              {metadataLoading ? 'Generating...' : 'Generate'}
            </Button>
          </ModalActions>
        </Modal>
      )}
    </>
  )
}
